<template>
  <div class="area-selector">
    <van-popup v-model="showPopup" position="bottom" round :style="{ height: '90%' }">
      <div class="area-selector-container">
        <div class="area-selector-header">
          <div class="area-selector-title">选择地区</div>
          <van-icon name="cross" @click="cancelSelect" />
        </div>
        <div class="area-selector-tabs">
          <div
            class="area-tab"
            :class="{ active: currentTab === 'province' }"
            @click="switchToProvince"
          >
            {{ selectedProvince || '请选择' }}
          </div>
          <div
            v-if="selectedProvince && hasNextLevel(selectedProvinceData)"
            class="area-tab"
            :class="{ active: currentTab === 'city' }"
            @click="switchToCity"
          >
            {{ selectedCity || '请选择' }}
          </div>
          <div
            v-if="selectedCity && hasNextLevel(selectedCityData)"
            class="area-tab"
            :class="{ active: currentTab === 'district' }"
            @click="switchToDistrict"
          >
            {{ selectedDistrict || '请选择' }}
          </div>
          <div
            v-if="selectedDistrict && hasNextLevel(selectedDistrictData)"
            class="area-tab"
            :class="{ active: currentTab === 'street' }"
            @click="switchToStreet"
          >
            {{ selectedStreet || '请选择' }}
          </div>
        </div>
        <div class="area-selector-content">
          <div v-if="currentTab === 'province'" class="area-list">
            <div
              v-for="item in provinceList"
              :key="item.id"
              class="area-item"
              @click="selectProvince(item)"
            >
              {{ item.name }}
            </div>
          </div>
          <div v-if="currentTab === 'city'" class="area-list">
            <div
              v-for="item in cityList"
              :key="item.id"
              class="area-item"
              @click="selectCity(item)"
            >
              {{ item.name }}
            </div>
          </div>
          <div v-if="currentTab === 'district'" class="area-list">
            <div
              v-for="item in districtList"
              :key="item.id"
              class="area-item"
              @click="selectDistrict(item)"
            >
              {{ item.name }}
            </div>
          </div>
          <div v-if="currentTab === 'street'" class="area-list">
            <div
              v-for="item in streetList"
              :key="item.id"
              class="area-item"
              @click="selectStreet(item)"
            >
              {{ item.name }}
            </div>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
export default {
  name: 'AreaSelector',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    // 初始选中的区域值
    initialArea: {
      type: Array,
      default: () => []
    },
    // HTTP请求函数
    httpRequest: {
      type: Function,
      required: true
    },
    // 父组件预加载的省份数据
    provinces: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showPopup: false,
      currentTab: 'province',
      provinceList: [],
      cityList: [],
      districtList: [],
      streetList: [],

      selectedProvince: '',
      selectedCity: '',
      selectedDistrict: '',
      selectedStreet: '',

      selectedProvinceData: null,
      selectedCityData: null,
      selectedDistrictData: null,
      selectedStreetData: null
    }
  },
  watch: {
    value(val) {
      this.showPopup = val
    },
    showPopup(val) {
      this.$emit('input', val)
    },
    // 监听父组件传入的省份数据变化
    provinces: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0) {
          this.provinceList = val
        }
      }
    }
  },
  methods: {
    // 加载省份数据
    async loadProvinces() {
      if (this.provinceList.length > 0) return // 如果已有省份数据则不重新加载

      try {
        const res = await this.httpRequest()
        this.provinceList = res.data || []
      } catch (error) {
        console.error('获取省份数据失败', error)
      }
    },

    // 加载城市数据
    async loadCities(province) {
      try {
        const params = {
          province: province.province || province.name
        }
        const res = await this.httpRequest(params)
        this.cityList = res.data || []
      } catch (error) {
        console.error('获取城市数据失败', error)
      }
    },

    // 加载区县数据
    async loadDistricts(province, city) {
      try {
        const params = {
          province: province.province || province.name,
          city: city.city || city.name
        }
        const res = await this.httpRequest(params)
        this.districtList = res.data || []
      } catch (error) {
        console.error('获取区县数据失败', error)
      }
    },

    // 加载街道数据
    async loadStreets(province, city, district) {
      try {
        const params = {
          province: province.province || province.name,
          city: city.city || city.name,
          area: district.area || district.name
        }
        const res = await this.httpRequest(params)
        this.streetList = res.data || []
      } catch (error) {
        console.error('获取街道数据失败', error)
      }
    },

    // 判断区县是否有下一级街道
    hasNextLevel(item) {
      return item && item.town && item.town === '0'
    },

    // 选择省份
    async selectProvince(item) {
      this.selectedProvince = item.name
      this.selectedProvinceData = item
      this.selectedCity = ''
      this.selectedCityData = null
      this.selectedDistrict = ''
      this.selectedDistrictData = null
      this.selectedStreet = ''
      this.selectedStreetData = null

      await this.loadCities(item)
      if (!this.cityList.length) this.closePopup()
      else this.currentTab = 'city'
    },

    // 选择城市
    async selectCity(item) {
      this.selectedCity = item.name
      this.selectedCityData = item
      this.selectedDistrict = ''
      this.selectedDistrictData = null
      this.selectedStreet = ''
      this.selectedStreetData = null

      await this.loadDistricts(this.selectedProvinceData, item)
      if (!this.districtList.length) this.closePopup()
      else this.currentTab = 'district'
    },

    // 选择区县
    async selectDistrict(item) {
      this.selectedDistrict = item.name
      this.selectedDistrictData = item
      this.selectedStreet = ''
      this.selectedStreetData = null

      // 检查是否有下一级街道
      if (this.hasNextLevel(item)) {
        // 加载街道数据
        await this.loadStreets(this.selectedProvinceData, this.selectedCityData, item)
        if (!this.streetList.length) this.closePopup()
        else this.currentTab = 'street'
      } else {
        // 没有下一级，直接关闭弹窗
        this.closePopup()
      }
    },

    // 选择街道
    selectStreet(item) {
      this.selectedStreet = item.name
      this.selectedStreetData = item
      this.closePopup()
    },

    closePopup() {
      const result = [
        this.selectedProvinceData,
        this.selectedCityData,
        this.selectedDistrictData,
        this.selectedStreetData
      ].filter(Boolean)

      this.$emit('confirm', result)
      this.showPopup = false
    },

    // 切换到省份选择
    switchToProvince() {
      this.currentTab = 'province'
    },

    // 切换到城市选择
    switchToCity() {
      if (this.selectedProvince) {
        this.currentTab = 'city'
      }
    },

    // 切换到区县选择
    switchToDistrict() {
      if (this.selectedCity) {
        this.currentTab = 'district'
      }
    },

    // 切换到街道选择
    switchToStreet() {
      if (this.selectedDistrict) {
        this.currentTab = 'street'
      }
    },

    // 取消选择
    cancelSelect() {
      this.showPopup = false
      this.$emit('cancel')
    },

    // 初始化已选区域
    initSelectedArea() {
      if (this.initialArea && this.initialArea.length > 0) {
        // 省
        if (this.initialArea[0]) {
          this.selectedProvince = this.initialArea[0]
          // 模拟省份数据结构
          this.selectedProvinceData = {
            name: this.initialArea[0],
            province: this.initialArea[0]
          }
          this.loadCities(this.selectedProvinceData)
        }

        // 市
        if (this.initialArea[1]) {
          this.selectedCity = this.initialArea[1]
          // 模拟城市数据结构
          this.selectedCityData = {
            name: this.initialArea[1],
            city: this.initialArea[1],
            province: this.initialArea[0]
          }
          this.loadDistricts(this.selectedProvinceData, this.selectedCityData)
        }

        // 区
        if (this.initialArea[2]) {
          this.selectedDistrict = this.initialArea[2]
          // 模拟区县数据结构
          this.selectedDistrictData = {
            name: this.initialArea[2],
            area: this.initialArea[2]
          }

          // 如果区县有下一级，才加载街道数据
          this.loadDistricts(this.selectedProvinceData, this.selectedCityData).then(() => {
            // 在区县数据加载完成后，查找当前选中的区县数据
            const currentDistrict = this.districtList.find(
              item => item.name === this.selectedDistrict
            )

            // 如果找到对应区县数据，且有下一级
            if (currentDistrict && this.hasNextLevel(currentDistrict)) {
              this.loadStreets(
                this.selectedProvinceData,
                this.selectedCityData,
                this.selectedDistrictData
              )
            }
          })
        }

        // 街道
        if (this.initialArea[3]) {
          this.selectedStreet = this.initialArea[3]
          // 模拟街道数据结构
          this.selectedStreetData = {
            name: this.initialArea[3],
            town: this.initialArea[3]
          }
        }
      }
    }
  },
  mounted() {
    // 如果父组件没有传入省份数据，则自己加载
    if (this.provinceList.length === 0) {
      this.loadProvinces()
    }
    // this.initSelectedArea()
  }
}
</script>

<style lang="scss" scoped>
.area-selector {
  &-container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;

    .area-selector-title {
      font-size: 16px;
      font-weight: bold;
    }
  }

  &-tabs {
    display: flex;
    padding: 0 16px 10px;
    flex-wrap: wrap;

    .area-tab {
      position: relative;
      margin-right: 24px;
      margin-bottom: 8px;
      padding: 8px 0;
      font-size: 14px;
      color: #666;

      &.active {
        color: #1989fa;
        font-weight: bold;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 2px;
          background-color: #1989fa;
        }
      }
    }
  }

  &-content {
    flex: 1;
    overflow-y: auto;

    .area-list {
      padding: 0 16px;

      .area-item {
        padding: 12px 0;
        font-size: 14px;
        border-bottom: 1px solid #f5f5f5;
      }
    }
  }
}
</style>
